import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight, FaEdit, FaTrash, FaUser, FaCalendarAlt, FaPercent, FaShoppingCart, FaUsers, FaClock, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useTitle } from '../../../context/TitleContext';
import { fetchPromoCodeDetails, deletePromoCode, clearError, clearSuccess } from '../../../redux/Slices/PromoCodesSlice';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { ToastContainer } from 'react-toastify';

const PharmacyPromoCodeDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { setTitle } = useTitle();

  const { promoCode, loading, error, success } = useSelector(state => state.promoCodes);

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    action: null,
    data: null
  });

  useEffect(() => {
    setTitle('تفاصيل البرومو كود');
    if (id) {
      dispatch(fetchPromoCodeDetails(id));
    }
  }, [dispatch, id, setTitle]);

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      showSuccessToast(success);
      dispatch(clearSuccess());
      if (confirmModal.action === 'delete') {
        navigate('/pharmacy-promo-codes');
      }
    }
  }, [success, dispatch, confirmModal.action, navigate]);

  const handleEdit = () => {
    navigate(`/pharmacy-promo-codes/${id}/edit`);
  };

  const handleDelete = () => {
    setConfirmModal({
      isOpen: true,
      action: 'delete',
      data: id
    });
  };

  const handleConfirmAction = () => {
    if (confirmModal.action === 'delete') {
      dispatch(deletePromoCode(confirmModal.data));
      setConfirmModal({ isOpen: false, action: null, data: null });
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = {
      1: { class: 'bg-success', text: 'نشط' },
      0: { class: 'bg-danger', text: 'غير نشط' },
      true: { class: 'bg-success', text: 'نشط' },
      false: { class: 'bg-danger', text: 'غير نشط' }
    };
    
    const currentStatus = statusInfo[status] || { class: 'bg-secondary', text: 'غير محدد' };
    
    return (
      <span className={`badge ${currentStatus.class} text-white fs-6`}>
        {currentStatus.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const isActive = (startsAt, expiresAt) => {
    const now = new Date();
    const start = new Date(startsAt);
    const end = new Date(expiresAt);
    return now >= start && now <= end;
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!promoCode) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <p className="text-muted">لم يتم العثور على البرومو كود</p>
          <button
            className="btn" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}
            onClick={() => navigate('/pharmacy-promo-codes')}
          >
            العودة للقائمة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <ToastContainer />
      
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">تفاصيل البرومو كود</h2>
        <div className="d-flex gap-2">
          <button
            className="btn d-flex align-items-center gap-2"
            onClick={handleEdit}
            style={{ backgroundColor: '#ffc107', color: 'white', border: 'none' }}
          >
            <FaEdit />
            تعديل
          </button>
          <button
            className="btn d-flex align-items-center gap-2"
            onClick={handleDelete}
            style={{ backgroundColor: '#dc3545', color: 'white', border: 'none' }}
          >
            <FaTrash />
            حذف
          </button>
          <button
            className="btn btn-secondary d-flex align-items-center gap-2"
            onClick={() => navigate('/pharmacy-promo-codes')}
          >
            <FaArrowRight />
            العودة
          </button>
        </div>
      </div>

      {/* Main Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card shadow-lg border-0"
        style={{ borderRadius: '20px', overflow: 'hidden' }}
      >
        <div className="card-body p-0">
          {/* Status Badges */}
          <div className="p-4 bg-light border-bottom">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-2">
                {getStatusBadge(promoCode.status)}
                {isExpired(promoCode.expires_at) && (
                  <span className="badge bg-warning text-dark fs-6">
                    <FaTimesCircle className="mx-1" />
                    منتهي الصلاحية
                  </span>
                )}
                {isActive(promoCode.starts_at, promoCode.expires_at) && (
                  <span className="badge bg-success text-white fs-6">
                    <FaCheckCircle className="mx-1" />
                    نشط حالياً
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="p-4">
            {/* Basic Information */}
            <div className="row mb-4">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <FaPercent className="mx-2 text-primary" />
                  المعلومات الأساسية
                </h5>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-dark" style={{ fontSize: '0.9rem' }}>كود البرومو:</label>
                    <div className="p-3 bg-light rounded-3 border">
                      <span className="fs-6 fw-bold text-primary">
                        {promoCode.code}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-dark" style={{ fontSize: '0.9rem' }}>نسبة الخصم:</label>
                    <div className="p-3 bg-light rounded-3 border">
                      <span className="fs-6 fw-bold text-success">
                        {Math.round(promoCode.discount_percent)}%
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-dark" style={{ fontSize: '0.9rem' }}>الحد الأدنى للطلب:</label>
                    <div className="p-3 bg-light rounded-3 border">
                      <span className="fs-6">
                        {promoCode.min_order || 0} جنيه
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-dark" style={{ fontSize: '0.9rem' }}>الحد الأقصى للطلب:</label>
                    <div className="p-3 bg-light rounded-3 border">
                      <span className="fs-6">
                        {promoCode.max_order ? `${promoCode.max_order} جنيه` : 'غير محدد'}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-dark" style={{ fontSize: '0.9rem' }}>حد الاستخدام:</label>
                    <div className="p-3 bg-light rounded-3 border">
                      <span className="fs-6">
                        {promoCode.usage_limit || 'غير محدود'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="row mb-4">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <FaCalendarAlt className="mx-2 text-success" />
                  التواريخ
                </h5>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-dark">تاريخ البداية:</label>
                    <div className="p-3 bg-light rounded-3 border">
                      <span className="fs-6">
                        {formatDate(promoCode.starts_at)}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-dark">تاريخ الانتهاء:</label>
                    <div className="p-3 bg-light rounded-3 border">
                      <span className="fs-6">
                        {formatDate(promoCode.expires_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Usage Statistics */}
            <div className="row mb-4">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <FaUsers className="mx-2 text-info" />
                  إحصائيات الاستخدام
                </h5>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-dark">عدد مرات الاستخدام:</label>
                    <div className="p-3 bg-light rounded-3 border">
                      <span className="fs-6 fw-bold text-primary">
                        {promoCode.used_count || 0}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-dark">المتبقي من الاستخدام:</label>
                    <div className="p-3 bg-light rounded-3 border">
                      <span className="fs-6 fw-bold text-success">
                        {promoCode.usage_limit ? 
                          Math.max(0, promoCode.usage_limit - (promoCode.used_count || 0)) : 
                          'غير محدود'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Creator Information */}
            <div className="row">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <FaUser className="mx-2 text-secondary" />
                  معلومات المنشئ
                </h5>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label fw-bold text-dark">نوع المنشئ:</label>
                    <div className="p-3 bg-light rounded-3 border">
                      <span className="badge bg-secondary text-white fs-6">
                        {promoCode.created_by_type || 'غير محدد'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: null, data: null })}
        onConfirm={handleConfirmAction}
        title="تأكيد الحذف"
        message="هل أنت متأكد من حذف هذا البرومو كود؟"
        confirmText="حذف"
        cancelText="إلغاء"
        type="danger"
      />
    </div>
  );
};

export default PharmacyPromoCodeDetails;
